import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home as HomeIcon, FolderKanban, Wand2, Clapperboard, Image as ImageIcon,
  Upload, Settings as SettingsIcon, LayoutGrid, Bell, Search, LogOut, Menu, X,
  Aperture, Film, Mic, AudioLines, Palette, LayoutTemplate, BookMarked,
  Share2, HardDrive, Activity as ActivityIcon, Scissors, ChevronDown,
  CreditCard, LifeBuoy, Sparkles, KeyRound, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import CinematicBackground from "@/components/CinematicBackground";
import Nova from "@/components/Nova";
import api from "@/lib/api";
import { cx } from "@/components/shared";
import { PRIMARY_NAV, STUDIO_ROOMS, SYSTEM_ROOMS } from "@/lib/studio";

const RAIL_ICONS = {
  home: HomeIcon, projects: FolderKanban, create: Wand2, studio: Clapperboard,
  assets: ImageIcon, export: Upload, settings: SettingsIcon,
};
const ROOM_ICONS = {
  planner: Aperture, video: Clapperboard, voice: Mic, sound: AudioLines,
  brand: Palette, templates: LayoutTemplate, prompts: BookMarked, editor: Scissors,
  publishing: Share2, storage: HardDrive, activity: ActivityIcon,
  billing: CreditCard, help: LifeBuoy,
};

function RailLink({ item, onNavigate }) {
  const Icon = RAIL_ICONS[item.icon] || HomeIcon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        cx(
          "group relative flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300",
          isActive
            ? "bg-[#5B4DFF] text-white shadow-[0_0_22px_rgba(91,77,255,0.55)]"
            : "text-slate-500 hover:text-white hover:bg-white/[0.06]"
        )
      }
    >
      <Icon className="w-[19px] h-[19px]" />
      <span className="pointer-events-none absolute left-[3.6rem] px-2.5 py-1 rounded-lg bg-[#12121f] border border-white/10 text-xs text-white whitespace-nowrap opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 z-50 shadow-xl">
        {item.label}
      </span>
    </NavLink>
  );
}

// Rooms launcher living in the LEFT rail (where Settings used to be)
function RailRooms() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const go = (to) => { navigate(to); setOpen(false); };
  const rooms = STUDIO_ROOMS;
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="rooms-launcher-button" title="Rooms"
        className={cx("group relative flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-300",
          open ? "bg-[#5B4DFF] text-white shadow-[0_0_22px_rgba(91,77,255,0.55)]" : "text-slate-500 hover:text-white hover:bg-white/[0.06]")}>
        <LayoutGrid className="w-[19px] h-[19px]" />
        <span className="pointer-events-none absolute left-[3.6rem] px-2.5 py-1 rounded-lg bg-[#12121f] border border-white/10 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition z-50 shadow-xl">Rooms</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.16 }}
            data-testid="rooms-launcher-menu"
            className="fixed left-[5.25rem] bottom-6 w-[300px] max-w-[80vw] max-h-[72vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#12121f]/95 backdrop-blur-xl p-2 z-[60] shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]">
            <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Studio Rooms</div>
            {rooms.map((r) => {
              const Icon = ROOM_ICONS[r.icon] || Film;
              return (
                <button key={r.to} onClick={() => go(r.to)} data-testid={`room-link-${r.icon}`}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.06] transition">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#5B4DFF]/12 border border-[#5B4DFF]/25 text-[#b6aaff]"><Icon className="w-4 h-4" /></span>
                  <span className="min-w-0"><span className="block text-sm text-white truncate">{r.label}</span><span className="block text-xs text-slate-500 truncate">{r.desc}</span></span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Narrow icon rail (desktop)
function IconRail() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center h-full py-5">
      <button onClick={() => navigate("/")} className="mb-6 shrink-0" data-testid="rail-logo">
        <Logo height={30} showWord={false} />
      </button>
      <nav className="flex-1 flex flex-col items-center gap-1.5">
        {PRIMARY_NAV.map((item) => <RailLink key={item.to} item={item} />)}
        <RailRooms />
      </nav>
      <div className="mt-4 mb-3 font-mono text-[8px] leading-[1.5] tracking-[0.2em] text-slate-600 text-center uppercase select-none">
        Ideas<br />People<br />Worlds<br />Cinema
      </div>
      <button
        onClick={logout}
        data-testid="logout-button"
        title="Log out"
        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center text-white text-sm font-semibold ring-2 ring-white/10 hover:ring-white/30 transition"
      >
        {(user?.name || "V")[0]}
      </button>
    </div>
  );
}

// Mobile expanded nav
function MobileNav({ onNavigate }) {
  const { logout, user } = useAuth();
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-5 h-20 shrink-0 border-b border-white/5">
        <Logo height={28} />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="px-3 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">Studio</div>
        <div className="space-y-0.5 mb-5">
          {PRIMARY_NAV.map((item) => {
            const Icon = RAIL_ICONS[item.icon] || HomeIcon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
                className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm",
                  isActive ? "bg-[#5B4DFF]/18 text-white border border-[#5B4DFF]/40" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent")}>
                <Icon className="w-[17px] h-[17px]" /> <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
        <div className="px-3 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">Production Rooms</div>
        <div className="space-y-0.5">
          {STUDIO_ROOMS.map((r) => {
            const Icon = ROOM_ICONS[r.icon] || Film;
            return (
              <NavLink key={r.to} to={r.to} onClick={onNavigate}
                className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
                  isActive ? "bg-[#5B4DFF]/18 text-white" : "text-slate-400 hover:text-white hover:bg-white/5")}>
                <Icon className="w-4 h-4 text-slate-500" /> <span>{r.label}</span>
              </NavLink>
            );
          })}
        </div>
        <div className="px-3 mt-5 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">Account</div>
        <div className="space-y-0.5">
          {SYSTEM_ROOMS.map((r) => {
            const Icon = ROOM_ICONS[r.icon] || Film;
            return (
              <NavLink key={r.to} to={r.to} onClick={onNavigate}
                className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
                  isActive ? "bg-[#5B4DFF]/18 text-white" : "text-slate-400 hover:text-white hover:bg-white/5")}>
                <Icon className="w-4 h-4 text-[#FFB300]" /> <span>{r.label}</span>
              </NavLink>
            );
          })}
          <NavLink to="/settings" onClick={onNavigate} data-testid="mobile-nav-settings"
            className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
              isActive ? "bg-[#5B4DFF]/18 text-white" : "text-slate-400 hover:text-white hover:bg-white/5")}>
            <SettingsIcon className="w-4 h-4 text-slate-500" /> <span>Account Settings</span>
          </NavLink>
          <NavLink to="/help" onClick={onNavigate} data-testid="mobile-nav-help"
            className={({ isActive }) => cx("flex items-center gap-3 px-3 py-2 rounded-xl text-sm",
              isActive ? "bg-[#5B4DFF]/18 text-white" : "text-slate-400 hover:text-white hover:bg-white/5")}>
            <LifeBuoy className="w-4 h-4 text-slate-500" /> <span>Help &amp; Support</span>
          </NavLink>
        </div>
      </nav>
      <div className="p-3 border-t border-white/5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#FFB300] grid place-items-center text-white text-sm font-semibold">{(user?.name || "V")[0]}</div>
        <div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{user?.name || "Director"}</div><div className="text-xs text-slate-500 truncate">{user?.email}</div></div>
        <button onClick={logout} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"><LogOut className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// Top-bar Rooms launcher
function RoomsLauncher() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="rooms-launcher-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-[#5B4DFF]/40 text-sm transition"
      >
        <LayoutGrid className="w-4 h-4" /> <span className="hidden sm:inline">Rooms</span>
        <ChevronDown className={cx("w-3.5 h-3.5 transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            data-testid="rooms-launcher-menu"
            className="!absolute right-0 top-full mt-2 w-[340px] max-w-[86vw] max-h-[74vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#12121f]/95 backdrop-blur-xl p-2 z-50 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]"
          >
            <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Production Rooms</div>
            <div className="grid grid-cols-1 gap-0.5">
              {STUDIO_ROOMS.map((r) => {
                const Icon = ROOM_ICONS[r.icon] || Film;
                return (
                  <button
                    key={r.to}
                    onClick={() => { navigate(r.to); setOpen(false); }}
                    data-testid={`room-link-${r.icon}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.06] transition group"
                  >
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#5B4DFF]/12 border border-[#5B4DFF]/25 text-[#b6aaff] group-hover:bg-[#5B4DFF]/22">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-white truncate">{r.label}</span>
                      <span className="block text-xs text-slate-500 truncate">{r.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-1 pt-1 border-t border-white/8">
              <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">Account</div>
              <div className="grid grid-cols-1 gap-0.5">
                {SYSTEM_ROOMS.map((r) => {
                  const Icon = ROOM_ICONS[r.icon] || Film;
                  return (
                    <button
                      key={r.to}
                      onClick={() => { navigate(r.to); setOpen(false); }}
                      data-testid={`room-link-${r.icon}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-white/[0.06] transition group"
                    >
                      <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#FFB300]/10 border border-[#FFB300]/25 text-[#FFB300] group-hover:bg-[#FFB300]/20">
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-white truncate">{r.label}</span>
                        <span className="block text-xs text-slate-500 truncate">{r.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const go = (to) => { navigate(to); setOpen(false); };
  const items = [
    ...(user?.role === "owner"
      ? [{ label: "Owner Dashboard", icon: ShieldCheck, action: () => go("/admin") }]
      : []),
    { label: "Account Settings", icon: SettingsIcon, action: () => go("/settings?section=workspace") },
    { label: "API Settings", icon: KeyRound, action: () => go("/settings?section=providers") },
    { label: "Membership & Credits", icon: CreditCard, action: () => go("/billing") },
    { label: "Help & Support", icon: LifeBuoy, action: () => go("/help") },
  ];
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2.5 pl-1" data-testid="header-avatar">
        <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#7C70FF] grid place-items-center text-white text-sm font-semibold ring-2 ring-white/10">
          {(user?.name || "V")[0]}
        </span>
        <span className="hidden md:block text-left leading-tight">
          <span className="block text-sm text-white truncate max-w-[120px]">{user?.name || "Director"}</span>
          <span className="block text-[11px] text-slate-500">Director</span>
        </span>
        <ChevronDown className={cx("hidden md:block w-4 h-4 text-slate-500 transition", open && "rotate-180")} />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              data-testid="user-menu"
              className="!absolute right-0 top-full mt-2 w-60 max-w-[86vw] rounded-2xl border border-white/10 bg-[#12121f]/95 backdrop-blur-xl p-2 z-50 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]"
            >
              <div className="px-3 py-2 border-b border-white/8 mb-1">
                <div className="text-sm text-white truncate">{user?.name || "Director"}</div>
                <div className="text-[11px] text-slate-500 truncate">{user?.email || "Studio owner"}</div>
              </div>
              {items.map((it) => (
                <button key={it.label} onClick={it.action} data-testid={`menu-${it.label.split(" ")[0].toLowerCase()}`}
                  className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-left text-sm text-slate-300 hover:text-white hover:bg-white/[0.06] transition">
                  <it.icon className="w-4 h-4 text-slate-500" /> {it.label}
                </button>
              ))}
              <button onClick={() => { logout(); }} data-testid="menu-logout"
                className="flex items-center gap-3 w-full px-3 py-2.5 mt-1 rounded-xl text-left text-sm text-red-300 hover:bg-red-500/10 transition border-t border-white/8">
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function StudioShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [providers, setProviders] = useState(null);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    api.get("/providers").then(({ data }) => setProviders(data)).catch(() => {});
    api.get("/credits/balance").then(({ data }) => setCredits(data)).catch(() => {});
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const engineActive = providers && (providers.text?.configured || providers.image?.configured);

  return (
    <div className="vyom-bg min-h-screen text-white cine-grain">
      <CinematicBackground />

      {/* Desktop icon rail */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-20 glass-strong border-r border-[#5B4DFF]/15 z-40">
        <IconRail />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)} className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" />
            <motion.aside initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 glass-strong z-50">
              <MobileNav onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main column */}
      <div className="lg:pl-20 relative z-10">
        <header className="sticky top-0 z-30 glass-strong border-b border-[#5B4DFF]/15">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden p-2 rounded-lg hover:bg-white/5" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="w-5 h-5" />
            </button>

            {/* Centered search */}
            <div className="flex items-center gap-2 flex-1 max-w-xl mx-auto">
              <div className="flex items-center gap-2 w-full px-3.5 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-400">
                <Search className="w-4 h-4" />
                <input data-testid="global-search-input" placeholder="Search projects, characters, ideas…"
                  className="bg-transparent outline-none text-sm w-full placeholder:text-slate-500 text-white" />
                <kbd className="hidden sm:inline font-mono text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10">⌘K</kbd>
              </div>
            </div>

            <button onClick={() => navigate("/billing")} data-testid="credit-chip"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FFB300]/10 border border-[#FFB300]/30 text-[#FFB300] text-sm font-semibold hover:bg-[#FFB300]/20 transition"
              title="Vyom credits — used when AI production actions run">
              <Sparkles className="w-3.5 h-3.5" />
              {credits ? credits.balance.toLocaleString() : "—"} <span className="hidden md:inline font-normal text-[#FFB300]/70">Credits</span>
            </button>

            <button onClick={() => navigate("/create")} data-testid="header-new-project-button"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5B4DFF] hover:bg-[#4B3DE6] text-white font-semibold text-sm transition shadow-[0_0_18px_rgba(91,77,255,0.45)]">
              <Wand2 className="w-4 h-4" /> New Film
            </button>

            <button className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid="notifications-button">
              <Bell className="w-5 h-5" />
              <span className={cx("absolute top-1.5 right-1.5 w-2 h-2 rounded-full", engineActive ? "bg-red-500" : "bg-slate-600")} />
            </button>

            <button onClick={() => navigate("/help")} data-testid="header-help-button" title="Help Center"
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5">
              <LifeBuoy className="w-5 h-5" />
            </button>

            <UserMenu />
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1440px] mx-auto relative overflow-x-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
              style={{ willChange: "opacity, transform" }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      <Nova />
    </div>
  );
}
